import { websocketService, WSMessageType } from '@/utils/websocketService';
import { usePermissionsStore } from '@/utils/permissionsStore';
import * as terminalApi from '@/api/terminal';
import { getAuthCapabilitiesSnapshot } from '@/utils/authCapabilities';
import React, { useEffect, useRef, useState } from 'react';
import { Terminal as Core } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import "../../node_modules/xterm/css/xterm.css";

const Terminal: React.FC = () => {
    const permissions = usePermissionsStore(state => state.permissions);
    const isLoadingPermissions = usePermissionsStore(state => state.isLoading);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const termInstance = useRef<Core | undefined>(undefined);
    const fitAddon = useRef<FitAddon | undefined>(undefined);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const ptyRetryCount = useRef(0);
    const PTY_MAX_RETRIES = 3;
    const [ptyFailed, setPtyFailed] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [failReason, setFailReason] = useState<'pty' | 'ws'>('pty');

    const canAccessTerminal = permissions['terminal'] === true;

    const handleTerminalOutput = (msg: any) => {
        if (msg?.data && termInstance.current) {
            termInstance.current.write(msg?.data);
        }
    };

    const resizeTerm = () => {
        if (!termInstance.current || !fitAddon.current) {
            return;
        }

        fitAddon.current.fit();
        websocketService.send({
            type: WSMessageType.TERMINAL_RESIZE,
            payload: {
                rows: termInstance.current.rows,
                cols: termInstance.current.cols,
            }
        });
    };

    const initTerminalHandler = () => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // If the container was recreated by layout updates, recreate xterm safely.
        if (termInstance.current && !container.querySelector('.xterm')) {
            termInstance.current.dispose();
            termInstance.current = undefined;
            fitAddon.current = undefined;
        }

        if (!termInstance.current) {
            const term = new Core({
                cursorBlink: true,
                theme: {
                    foreground: '#ECECEC',
                    background: '#000000',
                    cursor: 'help',
                }
            });
            const addon = new FitAddon();

            term.loadAddon(addon);
            term.open(container);

            term.attachCustomKeyEventHandler((event) => {
                // Ctrl+C copies selected text; otherwise keep shell interrupt behavior.
                if (event.ctrlKey && event.key === 'c') {
                    const selection = term.getSelection();
                    if (selection) {
                        event.preventDefault();
                        navigator.clipboard.writeText(selection);
                        return false;
                    }
                }
                if (event.ctrlKey && event.key === 'v') {
                    event.preventDefault();
                    navigator.clipboard.readText().then(text => {
                        websocketService.send({
                            type: WSMessageType.TERMINAL_INPUT,
                            payload: text,
                        });
                    });
                    return false;
                }
                return true;
            });

            term.onData(val => {
                websocketService.send({
                    type: WSMessageType.TERMINAL_INPUT,
                    payload: val,
                });
            });

            termInstance.current = term;
            fitAddon.current = addon;
        }

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                try {
                    resizeTerm();
                } catch (err) {
                    console.log(err);
                }
            });
        }

        resizeObserverRef.current.observe(container);
        setTimeout(() => {
            resizeTerm();
        }, 100);
    };

    useEffect(() => {
        if (!canAccessTerminal || isLoadingPermissions) {
            return;
        }

        initTerminalHandler();

        const handleReconnect = () => {
            console.log('[Terminal] WebSocket reconnected, re-attaching PTY');
            setTimeout(() => {
                // The service already re-sent terminal_init; send terminal_resize to sync dimensions.
                resizeTerm();
            }, 100);
        };

        const handleWsError = async (msg: { code: string; message: string }) => {
            if (msg.code === 'PTY_NOT_FOUND') {
                if (ptyRetryCount.current >= PTY_MAX_RETRIES) {
                    console.error('[Terminal] PTY_NOT_FOUND – max retries reached, giving up');
                    termInstance.current?.write('\r\n\x1b[31m[Terminal] Unable to connect to remote PTY.\x1b[0m\r\n');
                    setFailReason('pty');
                    setPtyFailed(true);
                    return;
                }
                const delay = Math.min(1000 * 2 ** ptyRetryCount.current, 16000); // 1s, 2s, 4s
                ptyRetryCount.current += 1;
                console.warn(`[Terminal] PTY_NOT_FOUND – retry ${ptyRetryCount.current}/${PTY_MAX_RETRIES} in ${delay}ms`);
                await new Promise(resolve => setTimeout(resolve, delay));
                try {
                    const res = await terminalApi.getPty();
                    if (res.ok && res.data?.pid) {
                        const pid = res.data.pid;
                        websocketService.setTerminalPid(pid);
                        websocketService.send({
                            type: WSMessageType.TERMINAL_INIT,
                            payload: {
                                terminalPid: pid,
                                rows: termInstance.current?.rows ?? 24,
                                cols: termInstance.current?.cols ?? 80,
                            },
                        });
                        ptyRetryCount.current = 0; // reset on success
                        setPtyFailed(false);
                        setFailReason('pty');
                    }
                } catch (e) {
                    console.error('[Terminal] Failed to re-provision PTY', e);
                }
            }
        };

        const handleDisconnected = (msg: { exhausted?: boolean }) => {
            if (msg?.exhausted) {
                console.warn('[Terminal] WS reconnect exhausted, showing retry button');
                setFailReason('ws');
                setPtyFailed(true);
            }
        };

        websocketService.on(WSMessageType.TERMINAL_OUTPUT, handleTerminalOutput);
        websocketService.on(WSMessageType.RECONNECTED, handleReconnect);
        websocketService.on(WSMessageType.ERROR, handleWsError);
        websocketService.on(WSMessageType.DISCONNECTED, handleDisconnected);

        return () => {
            websocketService.off(WSMessageType.TERMINAL_OUTPUT, handleTerminalOutput);
            websocketService.off(WSMessageType.RECONNECTED, handleReconnect);
            websocketService.off(WSMessageType.ERROR, handleWsError);
            websocketService.off(WSMessageType.DISCONNECTED, handleDisconnected);
            resizeObserverRef.current?.disconnect();
            resizeObserverRef.current = null;
            termInstance.current?.dispose();
            termInstance.current = undefined;
            fitAddon.current = undefined;
            ptyRetryCount.current = 0;
            setPtyFailed(false);
            setFailReason('pty');
        };
    }, [canAccessTerminal, isLoadingPermissions]);

    // 加载中显示占位符
    if (isLoadingPermissions) {
        return <div className='w-full h-full flex items-center justify-center text-gray-400 dark:text-[#858585]'>
            Loading permissions...
        </div>;
    }

    // 无权限时显示提示信息
    if (!canAccessTerminal) {
        // Read non-reactively: this branch renders long after startup, so the
        // single capabilities fetch has settled, and the surrounding component
        // already has an early-return hook order that is not worth disturbing.
        const offersVerification = getAuthCapabilitiesSnapshot().self_service.access_verification;
        return <div className='w-full h-full flex flex-col items-center justify-center text-gray-500 dark:text-[#999999]'>
            <p>🔒 {offersVerification
                ? 'This feature is available for Verified users.'
                : 'This feature requires permission from your administrator.'}</p>
            {offersVerification && (
                <p>👉 <a className='underline' href='/verification'>How to verify?</a></p>
            )}
        </div>;
    }

    const handleManualRetry = async () => {
        setIsRetrying(true);
        setPtyFailed(false);
        ptyRetryCount.current = 0;
        try {
            if (failReason === 'ws') {
                // WS 断线：重置计数并重新连接
                websocketService.reconnect();
            } else {
                // PTY 丢失：重新申请 PTY
                const res = await terminalApi.getPty();
                if (res.ok && res.data?.pid) {
                    const pid = res.data.pid;
                    websocketService.setTerminalPid(pid);
                    websocketService.send({
                        type: WSMessageType.TERMINAL_INIT,
                        payload: {
                            terminalPid: pid,
                            rows: termInstance.current?.rows ?? 24,
                            cols: termInstance.current?.cols ?? 80,
                        },
                    });
                } else {
                    setPtyFailed(true);
                }
            }
        } catch (e) {
            console.error('[Terminal] Manual retry failed', e);
            setPtyFailed(true);
        } finally {
            setIsRetrying(false);
        }
    };

    return (
        <div className="terminal-container page-container relative">
            {ptyFailed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black dark:bg-[#1E1E1E] bg-opacity-70 z-10 gap-3">
                    <span className="text-red-400 dark:text-[#F48771] text-sm">
                        {failReason === 'ws' ? 'Connection lost.' : 'Unable to connect to remote PTY.'}
                    </span>
                    <button
                        onClick={handleManualRetry}
                        disabled={isRetrying}
                        className="px-4 py-1.5 rounded bg-gray-700 dark:bg-[#333333] hover:bg-gray-600 dark:hover:bg-[#404040] text-white dark:text-[#FFFFFF] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isRetrying ? 'Reconnecting…' : 'Retry'}
                    </button>
                </div>
            )}
            <div ref={containerRef} id="terminal" data-testid="terminal">
            </div>
        </div>
    );
};

export default Terminal;